import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { LeagueScreen } from '@/features/league/components/LeagueScreen'
import { StandingsTableSkeleton } from '@/features/league/components/StandingsTableSkeleton'
import {
  leagueKeys,
  fetchLeagueGameweekStatus,
  fetchLeagueStandings,
} from '@/features/league/queries'
import { withLegacySkin } from '@/shared/components/layout/LegacySkin'

export const Route = createFileRoute('/_authenticated/league/$leagueId/')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === 'string' ? search.tab : 'tabela',
    gw:  typeof search.gw  === 'string' ? search.gw  : undefined,
  }),

  // Expose gw to the loader — TanStack Router v5 requires loaderDeps for search access
  loaderDeps: ({ search: { gw } }) => ({ gw }),

  // Prefetch GW status + standings before render — prevents blank screen
  loader: async ({ context: { queryClient }, params, deps }) => {
    const { leagueId } = params
    const { gw } = deps

    // allSettled: errors don't block navigation; component handles them
    await Promise.allSettled([
      queryClient.ensureQueryData({
        queryKey: leagueKeys.gameweekStatus(leagueId),
        queryFn:  () => fetchLeagueGameweekStatus(leagueId),
      }),
      queryClient.ensureQueryData({
        queryKey: leagueKeys.standings(leagueId, gw),
        queryFn:  () => fetchLeagueStandings(leagueId, gw),
      }),
    ])
  },

  // Shown while loader is running — prevents blank screen during navigation
  pendingComponent: withLegacySkin(LeagueLoadingSkeleton),

  component: withLegacySkin(LeagueIndexPage),
})

function LeagueLoadingSkeleton() {
  return (
    <div style={{ padding: '24px 16px' }}>
      <StandingsTableSkeleton />
    </div>
  )
}

function LeagueIndexPage() {
  const { leagueId } = Route.useParams()
  const { tab, gw } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  function handleTabChange(newTab: string) {
    void navigate({ search: (prev) => ({ ...prev, tab: newTab }), replace: true })
  }

  function handleGwChange(gwId: string | undefined) {
    void navigate({ search: (prev) => ({ ...prev, gw: gwId }), replace: true })
  }

  return (
    <LeagueScreen
      leagueId={leagueId}
      activeTab={tab}
      onTabChange={handleTabChange}
      {...(gw !== undefined ? { selectedGwId: gw } : {})}
      onGwChange={handleGwChange}
    />
  )
}
